import { Form, FormProps } from '@remix-run/react';
import { ComponentPropsWithoutRef, FC, useEffect, useId, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { useRootBodyScrollLock } from '../../hooks';
import { ModalBody, ModalContainer, ModalInner, ModalInnerProps } from './components';

export type ModalExitSource = 'cancel' | 'ok' | 'dangerous' | 'external';

export interface ModalProps
  extends Pick<ComponentPropsWithoutRef<'div'>, 'className' | 'aria-labelledby'>,
    Omit<ModalInnerProps, 'title' | 'titleId'> {
  isOpen: boolean;
  onExitComplete?: (modalExitSource?: ModalExitSource) => void;
  formProps?: Pick<FormProps, 'method' | 'action' | 'encType' | 'reloadDocument' | 'replace' | 'onSubmit'>;
  extraHiddenFields?: JSX.Element;
  children?: React.ReactNode;
  showActions?: boolean;
  /**
   * Rendered as the dialog's heading and used as its accessible name. When
   * omitted (children render their own heading) pass the heading's id via
   * aria-labelledby instead. A modal with neither is rendered without the
   * dialog role, since a dialog must have an accessible name.
   */
  title?: string;
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  cancel,
  children,
  title = '',
  'aria-labelledby': ariaLabelledBy,
  okText,
  onOk,
  okDisabled,
  onExitComplete,
  bodyText,
  checkboxText,
  className,
  okThemeType,
  okHTMLType,
  exitLocked,
  cancelDisabled,
  okLoading,
  formProps,
  extraHiddenFields,
  cancelText,
  dangerousDisabled,
  dangerousText,
  showActions = true,
}) => {
  const [locked, setLocked] = useState(false);
  const [modalExitSource, setExitSource] = useState<ModalExitSource>('external');
  const modalContentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // A blank title would label the dialog with a heading that has no accessible
  // name, so whitespace counts as absent. This one predicate decides both the
  // label below and (via the normalised title prop) whether the heading renders.
  const hasTitle = typeof title === 'string' && title.trim().length > 0;
  // Dialog semantics only when the dialog can be named. `aria-modal` is
  // deliberately not set until focus management lands (ELE-1099).
  const labelledBy = ariaLabelledBy ?? (hasTitle ? titleId : undefined);
  useClickAway(modalContentRef, () => {
    setExitSource('cancel');
    if (!exitLocked) cancel();
  });

  useEffect(() => setLocked(isOpen), [isOpen]);

  useRootBodyScrollLock(locked);

  const inner = (
    <ModalInner
      showActions={showActions}
      cancel={cancel}
      cancelDisabled={cancelDisabled}
      cancelText={cancelText}
      checkboxText={checkboxText}
      dangerousDisabled={dangerousDisabled}
      dangerousText={dangerousText}
      exitLocked={exitLocked}
      okDisabled={okDisabled}
      okHTMLType={formProps ? 'submit' : okHTMLType}
      okLoading={okLoading}
      okText={okText}
      okThemeType={okThemeType}
      //necessary to pass checked state to upstream consumers
      //does not impact exiting modal usages
      onOk={(checked) => {
        onOk?.(checked);
        setExitSource?.('ok');
      }}
      title={hasTitle ? title : ''}
      titleId={titleId}
      bodyText={bodyText}
      onCancelClick={() => setExitSource('cancel')}
      onDangerousClick={() => setExitSource('dangerous')}
    >
      {children}
    </ModalInner>
  );

  return (
    <ModalContainer
      isOpen={isOpen}
      onExitComplete={() => {
        setLocked(false);
        onExitComplete?.(modalExitSource);
      }}
    >
      <ModalBody
        ref={modalContentRef}
        className={className}
        role={labelledBy ? 'dialog' : undefined}
        aria-labelledby={labelledBy}
      >
        {formProps ? (
          <Form {...formProps} className="contents">
            {extraHiddenFields}
            {inner}
          </Form>
        ) : (
          inner
        )}
      </ModalBody>
    </ModalContainer>
  );
};
